/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */

import { v4 as uuid } from 'uuid';

import { actionProps, RecordedAction, Step, Story } from './LiveAgentTypes';

export const dedupeActions = (actions: RecordedAction[]): any[] => {
    const res: any[] = [];
    let startOfContiguousIndex: number | undefined;

    if (actions.length < 2) return actions;
    for (let i = 0; i < actions.length; i += 1) {
        const action = actions[i];
        if (action.name === 'typeText' || action.name === 'hover') {
            if (startOfContiguousIndex === undefined) {
                startOfContiguousIndex = i;
            } else {
                // If the hid or the action name is different from the previous one, we end the series
                if (action.hid !== actions[startOfContiguousIndex].hid || action.name !== actions[i - 1].name) {
                    res.push(actions[i - 1]); // A series of type-text or hover targeting the same element that ended
                    startOfContiguousIndex = i; // Starting a new series
                }
            }
            if (i === actions.length - 1) res.push(action);
        } else {
            // There was a series of type interrupted by a click
            if (startOfContiguousIndex !== undefined) res.push(actions[i - 1]);
            startOfContiguousIndex = undefined;
            res.push(action);
        }
    }
    return res;
};

export const trimHovers = (actions: RecordedAction[]): RecordedAction[] => {
    let i = actions.length - 1;
    while (i >= 0 && actions[i].name === 'hover') {
        i -= 1;
    }
    return actions.slice(0, i + 1);
};

export const getStepFromPublishableStep = (publishableStep: Step, blocks: Story[] = []) => {
    if (publishableStep.kind === 'block') {
        const block = blocks.find((b) => !b.archived && b.slug === publishableStep.slug);
        if (!block) return;
        return {
            ...publishableStep,
            blockSteps: block.steps.map((step): any => {
                const s = getStepFromPublishableStep(step);
                return { ...s, running: false, id: uuid() };
            }),
        };
    }
    return publishableStep;
};

export const getSeparatorPairs = (steps: Step[]) => {
    const separatorPairs = [];
    let pair = [];
    let currentHash: string | undefined | null = '';
    let currentPrompt: string | undefined | null = '';
    for (const [index, step] of steps.entries()) {
        if (step.kind === 'separator' && !currentHash) {
            currentHash = step.intentHash;
            currentPrompt = step.intentPrompt;
            pair.push(index);
        } else if (step.kind === 'separator' && currentHash && currentHash === step.intentHash) {
            pair.push(index);
            separatorPairs.push({ intentHash: currentHash, intentPrompt: currentPrompt, pair });
            currentHash = '';
            currentPrompt = '';
            pair = [];
        }
    }
    return separatorPairs;
};

export type KeyValues = 'target' | 'value' | 'operator' | 'assetType' | 'assetUrl';
export type ParamsValues =
    | 'value'
    | 'target'
    | 'intent'
    | 'signupMethod'
    | 'operator'
    | 'input'
    | 'typingMode'
    | 'address';
const optionalParams = ['intent', 'typingMode'];
export type ParamsMap = Record<KeyValues, ParamsValues>;

export const getStepParams = (
    step: Step,
    paramsMap: ParamsMap,
): Partial<Record<ParamsValues | 'assetType' | 'assetUrl', string>> => {
    const stepParams: Partial<Record<ParamsValues | 'assetType' | 'assetUrl', string>> = {};
    for (const key of Object.keys(paramsMap) as Array<keyof typeof paramsMap>) {
        if (!step[paramsMap[key]]) {
            continue;
        }
        stepParams[key] = step[paramsMap[key]];
    }
    if (step.mediaAssets) {
        const assetType = Object.keys(step.mediaAssets)[0];
        stepParams.assetType = assetType;
        stepParams.assetUrl = step.mediaAssets[assetType].url;
    }

    return stepParams;
};

export const isStepExecutable = (step: Step): boolean => {
    try {
        const stepType = step.action || step.kind;
        if (stepType === 'empty') {
            return false;
        }

        if (stepType === 'emailSignup') {
            if (step.intent === 'confirmationCode-save') {
                return !!step.saveCodeToVariable;
            }
            return !!step.signupMethod;
        }

        const { paramsMap } = actionProps[stepType as keyof typeof actionProps];

        const stepParams = getStepParams(step, paramsMap as ParamsMap);
        const mandatoryParams = Object.keys(paramsMap).filter(
            (key) => !optionalParams.includes(paramsMap[key as keyof typeof paramsMap]),
        );
        return mandatoryParams.every((key) => stepParams[key as keyof typeof stepParams]);
    } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Error in isStepExecutable', e);
        return false;
    }
};

export const createSeparatorStep = (intentHash: string, intentPrompt: string): Step => ({
    kind: 'separator',
    id: uuid(),
    intentHash,
    intentPrompt,
    actions: [],
});
